import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Link } from 'react-router-dom';
import history from '../../../../history';

// Antd
import Highlighter from 'react-highlight-words';
import {
    SearchOutlined,
    PlusOutlined,
    DownloadOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    TeamOutlined,
    BankOutlined,
    BookOutlined
} from '@ant-design/icons';
import { Button, Input, Space, Spin, Table, Tag, Segmented } from 'antd';

// API
import { getAllUsers } from '../../../Constants/Api/functions/function';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { setAllUsers } from '../../../Reducers/allUsersReducer';

// Styles
import './style.scss';
import AddUser from './AddUser'

const Users = () => {
    const [searchText, setSearchText] = useState('');
    const [tableData, setTableData] = useState([]);
    const [searchedColumn, setSearchedColumn] = useState('');
    const [isAddUserOpen, setIsAddUserOpen] = useState(false);
    const [users, setUsers] = useState([])
    const [profileStatusFilter, setProfileStatusFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(false)
    const searchInput = useRef(null);
    const dispatch = useDispatch();
    const allUsers = useSelector(state => state.allUsers.allUsers);
    const userProfile = useSelector(state => state.userProfile.userProfile);

    useEffect(() => {
        if (userProfile?.role !== 'superadmin' && userProfile?.role !== 'admin') {
            history.push('/dashboard/home')
        }
    }, [userProfile])

    useEffect(() => {
        // Always fetch on component mount
        handleGetAllUsers()
    }, [])

    useEffect(() => {
        if (allUsers && Array.isArray(allUsers)) {
            setUsers(allUsers)
        }
    }, [allUsers])

    useEffect(() => {
        if (users?.length) {
            const data = users?.map((user, index) => {
                return {
                    key: index + 1,
                    id: user?.id,
                    indexNumber: user?.indexNumber,
                    centerId: user?.centerId,
                    name: user?.name,
                    Role: user?.role,
                    email: user?.email,
                    contactNumber: user?.contactNumber,
                    address: user?.address,
                    companyName: user?.companyName || (user?.isEmployed === 0 ? 'Full-Time Student' : '-'),
                    companyEmail: user?.companyEmail || '-',
                    companyDesignation: user?.companyDesignation || '-',
                    companyMobile: user?.companyMobile || '-',
                    companyAddress: user?.companyAddress || '-',
                    isEmployed: user?.isEmployed,
                    isProfileCompleted: user?.isProfileCompleted,
                }
            })
            setTableData(data)
        }
    }, [users])

    const handleGetAllUsers = async () => {
        setIsLoading(true)
        try {
            if (userProfile?.role === 'superadmin') {
                const response = await getAllUsers();
                if (response && Array.isArray(response)) {
                    // Create new array references to ensure React detects changes
                    dispatch(setAllUsers([...response]))
                    setUsers([...response])
                }
            } else if (userProfile?.role === 'admin' && userProfile?.centerId) {
                // Admin can only see users from their center
                const response = await getAllUsers(userProfile.centerId);
                if (response && Array.isArray(response)) {
                    // Create new array references to ensure React detects changes
                    dispatch(setAllUsers([...response]))
                    setUsers([...response])
                }
            }
        } catch (error) {
            console.error('getAllUsers', error);
        } finally {
            setIsLoading(false)
        }
    }

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                            setSearchText(selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1677ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const getNameSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                            setSearchText(selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1677ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text, record) =>
            searchedColumn === dataIndex ? (
                <Link to={`/dashboard/users/${record.id}/overview`}>
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                </Link>
            ) : (
                <Link to={`/dashboard/users/${record.id}/overview`}>
                    <p
                        style={{
                            color: '#1677ff',
                            cursor: 'pointer',
                        }}
                    >
                        {text}
                    </p>
                </Link>
            ),
    });

    const columns = [
        {
            title: 'User Name',
            dataIndex: 'name',
            key: 'name',
            width: '20%',
            ...getNameSearchProps('name'),
        },
        {
            title: 'Index Number',
            dataIndex: 'indexNumber',
            key: 'indexNumber',
            width: '12%',
            sorter: (a, b) => a.indexNumber - b.indexNumber,
            ...getColumnSearchProps('indexNumber'),
        },
        {
            title: 'Center Id',
            dataIndex: 'centerId',
            key: 'centerId',
            width: '10%',
            sorter: (a, b) => a.centerId - b.centerId,
            ...getColumnSearchProps('centerId'),
        },
        {
            title: 'Contact No',
            dataIndex: 'contactNumber',
            key: 'contactNumber',
            width: '12%',
            ...getColumnSearchProps('contactNumber'),
        },
        {
            title: 'Role',
            dataIndex: 'Role',
            key: 'Role',
            width: '10%',
            filters: [
                {
                    text: 'Super Admin',
                    value: 'superadmin',
                },
                {
                    text: 'Admin',
                    value: 'admin',
                },
                {
                    text: 'Student',
                    value: 'student',
                },
            ],
            onFilter: (value, record) => record.Role.indexOf(value) === 0,
            render: (text) => {
                let color = 'geekblue';
                if (text === 'superadmin') {
                    color = 'volcano';
                } else if (text === 'admin') {
                    color = 'orange';
                } else if (text === 'student') {
                    color = 'blue';
                }
                return (
                    <Tag color={color} key={text}>
                        {text.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: '18%',
            ...getColumnSearchProps('email'),
        },
        {
            title: 'Category',
            dataIndex: 'isEmployed',
            key: 'isEmployed',
            width: '13%',
            filters: [
                { text: 'Employed', value: 1 },
                { text: 'Full-Time Student', value: 0 },
            ],
            onFilter: (value, record) => {
                if (record.Role !== 'student') return false;
                if (value === 1) return Number(record.isProfileCompleted) === 1 && record.isEmployed !== 0;
                if (value === 0) return Number(record.isProfileCompleted) === 1 && (record.isEmployed === 0 || record.isEmployed === false);
                return true;
            },
            render: (isEmployed, record) => {
                if (record.Role !== 'student') return <Tag color="default">Staff</Tag>;
                if (!record.isProfileCompleted) return <Tag color="orange">Pending Info</Tag>;
                if (isEmployed === 0 || isEmployed === false) {
                    return <Tag color="cyan">Full-Time Student</Tag>;
                }
                return <Tag color="green">Employed</Tag>;
            }
        },
        {
            title: 'Company',
            dataIndex: 'companyName',
            key: 'companyName',
            width: '14%',
            ...getColumnSearchProps('companyName'),
            render: (text, record) => {
                if (record.Role === 'student' && (record.isEmployed === 0 || record.isEmployed === false)) {
                    return <span style={{ color: '#0284c7', fontStyle: 'italic' }}>Full-Time Student</span>;
                }
                return text && text !== '-' ? text : '-';
            }
        },
        {
            title: 'Designation',
            dataIndex: 'companyDesignation',
            key: 'companyDesignation',
            width: '13%',
            ...getColumnSearchProps('companyDesignation'),
            render: (text, record) => {
                if (record.Role === 'student' && (record.isEmployed === 0 || record.isEmployed === false)) {
                    return <span style={{ color: '#94a3b8' }}>N/A (Student)</span>;
                }
                return text && text !== '-' ? text : '-';
            }
        },
        {
            title: 'Profile Status',
            dataIndex: 'isProfileCompleted',
            key: 'isProfileCompleted',
            width: '12%',
            filters: [
                { text: 'Completed', value: 1 },
                { text: 'Pending', value: 0 }
            ],
            onFilter: (value, record) => Number(record.isProfileCompleted || 0) === Number(value),
            render: (completed, record) => {
                if (record.Role !== 'student') return <Tag color="default">N/A</Tag>;
                return completed ? (
                    <Tag color="success">Completed</Tag>
                ) : (
                    <Tag color="warning">Pending</Tag>
                );
            }
        },
    ];

    // Compute stats
    const studentList = users.filter(u => u?.role === 'student');
    const completedList = studentList.filter(u => Number(u?.isProfileCompleted) === 1);
    const pendingList = studentList.filter(u => !u?.isProfileCompleted);
    const employedList = completedList.filter(u => u?.isEmployed !== 0);
    const fullTimeList = completedList.filter(u => u?.isEmployed === 0 || u?.isEmployed === false);
    const completionRate = studentList.length > 0 ? Math.round((completedList.length / studentList.length) * 100) : 0;

    // Filter table data based on active status filter
    const displayedTableData = useMemo(() => {
        if (profileStatusFilter === 'completed') {
            return tableData.filter(u => Number(u.isProfileCompleted) === 1);
        }
        if (profileStatusFilter === 'pending') {
            return tableData.filter(u => u.Role === 'student' && !u.isProfileCompleted);
        }
        if (profileStatusFilter === 'employed') {
            return tableData.filter(u => Number(u.isProfileCompleted) === 1 && u.isEmployed !== 0);
        }
        if (profileStatusFilter === 'fulltime') {
            return tableData.filter(u => Number(u.isProfileCompleted) === 1 && (u.isEmployed === 0 || u.isEmployed === false));
        }
        return tableData;
    }, [tableData, profileStatusFilter]);

    const handleExportUsers = () => {
        const exportData = profileStatusFilter !== 'all' ? displayedTableData : tableData;
        const headers = [
            'id',
            'indexNumber',
            'name',
            'Role',
            'email',
            'contactNumber',
            'isEmployed',
            'companyName',
            'companyEmail',
            'companyDesignation',
            'companyMobile',
            'companyAddress',
            'isProfileCompleted'
        ];

        const csvContent =
            "data:text/csv;charset=utf-8," +
            headers.join(",") +
            "\n" +
            exportData.map((user) => {
                return headers.map((header) => {
                    const val = user[header] !== undefined && user[header] !== null
                        ? `"${String(user[header]).replace(/"/g, '""')}"`
                        : '""';
                    return val;
                }).join(",");
            }).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `users_${profileStatusFilter}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return <div className='users'>
        {/* Profile Statistics Summary Cards */}
        <div className="users-stats-row">
            <div
                className={`stat-card ${profileStatusFilter === 'all' ? 'active' : ''}`}
                onClick={() => setProfileStatusFilter('all')}
            >
                <div className="stat-content">
                    <div className="stat-label">Total Users</div>
                    <div className="stat-value">{users?.length || 0}</div>
                </div>
                <div className="stat-icon-wrap blue">
                    <TeamOutlined />
                </div>
            </div>

            <div
                className={`stat-card ${profileStatusFilter === 'completed' ? 'active' : ''}`}
                onClick={() => setProfileStatusFilter('completed')}
            >
                <div className="stat-content">
                    <div className="stat-label">Completed Profiles</div>
                    <div className="stat-value">
                        {completedList.length}
                        <span className="stat-subtext">({completionRate}%)</span>
                    </div>
                </div>
                <div className="stat-icon-wrap green">
                    <CheckCircleOutlined />
                </div>
            </div>

            <div
                className={`stat-card ${profileStatusFilter === 'employed' ? 'active' : ''}`}
                onClick={() => setProfileStatusFilter('employed')}
            >
                <div className="stat-content">
                    <div className="stat-label">Employed Students</div>
                    <div className="stat-value">{employedList.length}</div>
                </div>
                <div className="stat-icon-wrap purple">
                    <BankOutlined />
                </div>
            </div>

            <div
                className={`stat-card ${profileStatusFilter === 'fulltime' ? 'active' : ''}`}
                onClick={() => setProfileStatusFilter('fulltime')}
            >
                <div className="stat-content">
                    <div className="stat-label">Full-Time Students</div>
                    <div className="stat-value">{fullTimeList.length}</div>
                </div>
                <div className="stat-icon-wrap cyan">
                    <BookOutlined />
                </div>
            </div>

            <div
                className={`stat-card ${profileStatusFilter === 'pending' ? 'active' : ''}`}
                onClick={() => setProfileStatusFilter('pending')}
            >
                <div className="stat-content">
                    <div className="stat-label">Pending Profiles</div>
                    <div className="stat-value">{pendingList.length}</div>
                </div>
                <div className="stat-icon-wrap amber">
                    <ClockCircleOutlined />
                </div>
            </div>
        </div>

        <div className='users-action-buttons'>
            <h2>
                Users List <Tag
                    style={{
                        transform: 'translateY(-3px)',
                        marginLeft: '10px'
                    }}
                    color="blue">{displayedTableData?.length}</Tag>
            </h2>
            {(userProfile?.role === 'superadmin' || userProfile?.role === 'admin') &&
                <div className='actions'>
                    {userProfile?.role === 'superadmin' && (
                        <Button
                            type='default'
                            onClick={handleExportUsers}
                        >
                            <DownloadOutlined />
                            Export {profileStatusFilter === 'all' ? 'Users' : `${profileStatusFilter.toUpperCase()} (${displayedTableData.length})`}
                        </Button>
                    )}

                    <Button
                        type='primary'
                        onClick={() => setIsAddUserOpen(true)}
                    >
                        <PlusOutlined />
                        Add User
                    </Button>
                </div>}
        </div>

        {/* Filter Tabs */}
        <div className="users-filter-bar">
            <Segmented
                value={profileStatusFilter}
                onChange={(val) => setProfileStatusFilter(val)}
                options={[
                    { label: `All Users (${tableData.length})`, value: 'all' },
                    { label: `Completed Profiles (${completedList.length})`, value: 'completed' },
                    { label: `Employed (${employedList.length})`, value: 'employed' },
                    { label: `Full-Time Students (${fullTimeList.length})`, value: 'fulltime' },
                    { label: `Pending Profiles (${pendingList.length})`, value: 'pending' },
                ]}
            />
        </div>

        <Spin
            spinning={isLoading}
            tip="Loading Users..."
        >
            <Table
                className='users_table'
                columns={columns}
                dataSource={displayedTableData}
                expandable={{
                    expandedRowRender: (record) => (
                        <div className="user-expanded-company-details">
                            <div className="expanded-title">
                                <BankOutlined style={{ color: '#2563eb' }} /> Company & Employment Information
                                {record.isProfileCompleted ? (
                                    <Tag color="success" style={{ marginLeft: 8 }}>Profile Completed</Tag>
                                ) : (
                                    <Tag color="warning" style={{ marginLeft: 8 }}>Profile Pending</Tag>
                                )}
                            </div>
                            <div className="details-grid">
                                <div className="detail-item">
                                    <span className="detail-label">Employment Status</span>
                                    <span className="detail-val">
                                        {record.isEmployed === 0 ? 'Full-Time Student' : (record.isProfileCompleted ? 'Employed' : 'Pending Verification')}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Company Name</span>
                                    <span className="detail-val">{record.companyName}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Company Work Email</span>
                                    <span className="detail-val">{record.companyEmail}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Designation</span>
                                    <span className="detail-val">{record.companyDesignation}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="detail-label">Company Mobile No.</span>
                                    <span className="detail-val">{record.companyMobile}</span>
                                </div>
                                <div className="detail-item" style={{ gridColumn: 'span 2' }}>
                                    <span className="detail-label">Company Address</span>
                                    <span className="detail-val">{record.companyAddress}</span>
                                </div>
                            </div>
                        </div>
                    ),
                    rowExpandable: (record) => record.Role === 'student',
                }}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: false,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
                }}
            />
        </Spin>
        <AddUser
            isOpen={isAddUserOpen}
            setOpen={setIsAddUserOpen}
            handleGetAllUsers={handleGetAllUsers}
        />
    </div>;
};
export default Users;